/**
 * Kontrakt FE silnika widgetów (Faza 23) — lustro serwera:
 *  - `apps/api/lib/widgets/types.ts` (typy wizualne, funkcje agregujące, kubełki dat, wynik compute),
 *  - `apps/api/lib/widgets/config-schema.ts` (kształt `config` definicji).
 *
 * Współdzielony przez kreator (definicje w ustawieniach tenanta) i host pulpitu (kafel `custom`).
 * Trzymamy go w jednym miejscu, żeby nie dryfować względem backendu.
 */

import type { FilterOperator } from '@saas/shared';

/** Domena kolumny `widget_definitions.visual_type`. */
export const WIDGET_VISUAL_TYPES = ['kpi', 'bar', 'pie', 'donut', 'line', 'table'] as const;
export type WidgetVisualType = (typeof WIDGET_VISUAL_TYPES)[number];

/** Funkcje agregujące MVP (podzbiór z @saas/shared zaimplementowany w silniku). */
export const WIDGET_AGGREGATE_FNS = [
  'count',
  'sum',
  'avg',
  'min',
  'max',
  'count_distinct',
] as const;
export type WidgetAggregateFn = (typeof WIDGET_AGGREGATE_FNS)[number];

/** Kubełkowanie dat przy grupowaniu (oś czasu wykresów). */
export const WIDGET_DATE_BUCKETS = ['day', 'week', 'month', 'quarter', 'year'] as const;
export type WidgetDateBucket = (typeof WIDGET_DATE_BUCKETS)[number];

export interface WidgetAggregateSpec {
  fn: WidgetAggregateFn;
  /** `count` nie wymaga pola; pozostałe funkcje wymagają pola z whitelisty źródła. */
  field?: string;
}

export interface WidgetGroupBySpec {
  field: string;
  /** Dla kolumn datowych obowiązkowy kubełek. */
  dateBucket?: WidgetDateBucket;
  /** Limit liczby grup (reszta → kubeł „inne"). */
  maxItems?: number;
}

export interface WidgetTableSpec {
  columns: string[];
  sort?: { field: string; direction: 'asc' | 'desc' };
  limit: number;
}

export interface WidgetFilterSpec {
  field: string;
  operator: FilterOperator;
  value?: unknown;
}

/**
 * `config` definicji (JSONB) — lustro `widgetDefinitionConfigSchema`. Wszystkie pola opcjonalne;
 * kombinację poprawną dla danego typu wizualnego (kpi → aggregate; wykres → aggregate+groupBy;
 * table → table) egzekwuje silnik, nie ten typ.
 */
export interface WidgetDefinitionConfig {
  aggregate?: WidgetAggregateSpec;
  groupBy?: WidgetGroupBySpec;
  table?: WidgetTableSpec;
  filters?: WidgetFilterSpec[];
}

/** Wiersz katalogu definicji (`widget_definitions`) zwracany przez `/api/widget-definitions`. */
export interface WidgetDefinitionDTO {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  visualType: WidgetVisualType;
  source: string;
  config: WidgetDefinitionConfig;
  compatibleEntities: string[];
  isActive: boolean;
  /** Znacznik miękkiego skasowania (ISO); null = aktywna. Widoczne tylko przy `include_deleted`. */
  gcrecord: string | null;
}

// ── Metadane źródeł (trasa `GET /api/widget-definitions/sources`) — dla kreatora ───────────────

/** Rodzaj kolumny core — lustro `CoreColumnKind` (apply-header-filters) używanego przez silnik. */
export type WidgetFieldKind =
  | 'text'
  | 'date'
  | 'timestamp'
  | 'numeric'
  | 'boolean'
  | 'uuid'
  | 'unknown';

export interface WidgetSourceFieldMeta {
  slug: string;
  kind: WidgetFieldKind;
  /** Ludzka etykieta pola z katalogu (fallback: slug). */
  label: string;
}

/** Opis jednego źródła dla kreatora — pola do metryk/filtrów/kolumn + wskazówki grupowania. */
export interface WidgetSourceMeta {
  key: string;
  permissionCode: string;
  scope: 'company' | 'tenant';
  /** Domyślne pole daty (oś czasu) — null gdy źródło nie ma naturalnej osi. */
  defaultDateField: string | null;
  /** Encje-rodzice, na których formularzu wolno osadzić widget kontekstowy. */
  contextEntities: string[];
  /** Slug pola-powiązania → kartoteka etykiety (grupowanie „wg nazwy"). */
  referenceFields: Record<string, string>;
  fields: WidgetSourceFieldMeta[];
}

// ── Wynik compute (endpoint danych i podglądu) — lustro `WidgetComputeResult` ──────────────────

export interface WidgetSeriesItem {
  key: string;
  label: string;
  value: number;
}

export type WidgetComputeValue =
  | { kind: 'scalar'; value: number | null }
  | { kind: 'series'; items: WidgetSeriesItem[]; truncated: boolean }
  | {
      kind: 'rows';
      columns: { field: string; label: string }[];
      rows: Record<string, unknown>[];
    };

export type WidgetComputeFailure =
  | { code: 'SOURCE_UNKNOWN'; source: string }
  | { code: 'PERMISSION_DENIED' }
  | { code: 'INVALID_COMPANY_ID' }
  | { code: 'FIELD_NOT_ALLOWED'; field: string }
  | { code: 'CONTEXT_NOT_SUPPORTED'; entity: string }
  | { code: 'UNSUPPORTED'; reason: string };

export type WidgetComputeResult =
  | { ok: true; value: WidgetComputeValue }
  | { ok: false; failure: WidgetComputeFailure };

/**
 * Odpowiedź `POST /widget-definitions/:id/data` — `WidgetComputeResult` wzbogacony o
 * `visualType` definicji (host formularza i pulpit czytają je stąd zamiast zgadywać/snapshotować).
 * Pole opcjonalne — jak prop `visualType` w `WidgetResultView` — więc istniejące mocki
 * `WidgetComputeResult` w testach, które go nie ustawiają, zostają poprawne.
 */
export type WidgetDataApiResponse = WidgetComputeResult & { visualType?: WidgetVisualType };
